import {
  wgs84DecimalToDms,
  wgs84DecimalToUtm,
  type Wgs84Dms,
  type Wgs84Utm,
} from "@/lib/geo/wgs84-conversions";

/** Which coordinate representations to show in location tables and detail pages (WGS84). */
export type LocationCoordinateDisplays = {
  decimal: boolean;
  dms: boolean;
  utm: boolean;
};

export const defaultLocationCoordinateDisplays: LocationCoordinateDisplays = {
  decimal: true,
  dms: false,
  utm: false,
};

export function hasAtLeastOneCoordinateDisplay(d: LocationCoordinateDisplays): boolean {
  return d.decimal || d.dms || d.utm;
}

/** First enabled display order: decimal → DMS → UTM (aligns with location lists). */
export type CoordinateInputMode = "decimal" | "dms" | "utm";

export function defaultCoordinateInputMode(d: LocationCoordinateDisplays): CoordinateInputMode {
  if (d.decimal) return "decimal";
  if (d.dms) return "dms";
  if (d.utm) return "utm";
  return "decimal";
}

function formatDmsLine(dms: Wgs84Dms): string {
  const fmt = (deg: number, min: number, sec: number) => `${deg}° ${min}' ${sec.toFixed(2)}"`;
  return `${fmt(dms.latitude.degrees, dms.latitude.minutes, dms.latitude.seconds)} ${dms.latitude.hemisphere}, ${fmt(dms.longitude.degrees, dms.longitude.minutes, dms.longitude.seconds)} ${dms.longitude.hemisphere}`;
}

function formatUtmLine(utm: Wgs84Utm): string {
  return `Z${utm.zone}${utm.hemisphere} · E ${utm.easting.toFixed(1)} · N ${utm.northing.toFixed(1)}`;
}

export type FormattedCoordinateLine = { id: "decimal" | "dms" | "utm"; text: string };

/**
 * Builds display lines in stable order (decimal, DMS, UTM) for the enabled modes.
 * Invalid lat/lon skips non-decimal lines if conversion throws.
 */
export function formatLocationCoordinateLines(
  lat: number,
  lon: number,
  displays: LocationCoordinateDisplays,
): FormattedCoordinateLine[] {
  const lines: FormattedCoordinateLine[] = [];
  const d = { lat, lon };

  if (displays.decimal) {
    lines.push({
      id: "decimal",
      text: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
    });
  }

  if (displays.dms) {
    try {
      const dms = wgs84DecimalToDms(d);
      lines.push({ id: "dms", text: formatDmsLine(dms) });
    } catch {
      lines.push({ id: "dms", text: "—" });
    }
  }

  if (displays.utm) {
    try {
      const utm = wgs84DecimalToUtm(d);
      lines.push({ id: "utm", text: formatUtmLine(utm) });
    } catch {
      lines.push({ id: "utm", text: "—" });
    }
  }

  if (lines.length <= 1) {
    return lines;
  }

  const label: Record<FormattedCoordinateLine["id"], string> = {
    decimal: "Decimal",
    dms: "DMS",
    utm: "UTM",
  };

  return lines.map((row) => ({
    ...row,
    text: `${label[row.id]} · ${row.text}`,
  }));
}
